import type { FieldPack } from '../../../../core/fields/types';
import { FieldType } from '../../../../core/fields/types';

export const orgchartDemographicsFieldPack: FieldPack = {
  id: 'orgchart-demographics',
  name: 'Demographics (Planning Overlay)',
  description: 'Diversity and demographic data used as context overlay during planning workflows',
  version: '1.0.0',
  dependencies: ['orgchart-base'],
  fields: [
    {
      id: 'gender',
      label: 'Gender',
      type: FieldType.SELECT,
      tags: ['demographics', 'dei'],
      priority: 80,
      namespace: 'demographics',
      options: [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' },
        { value: 'non_binary', label: 'Non-Binary' },
        { value: 'prefer_not_to_say', label: 'Prefer Not to Say' },
      ],
      sensitivity: 'sensitive_pii',
    },
    {
      id: 'pronouns',
      label: 'Pronouns',
      // Common sets are offered for convenience, never as a closed list —
      // custom entry is the point of the field, so it must stay reachable.
      type: FieldType.SELECT,
      allowCustomOptions: true,
      tags: ['demographics', 'dei'],
      priority: 75,
      namespace: 'demographics',
      options: [
        { value: 'she/her', label: 'she/her' },
        { value: 'he/him', label: 'he/him' },
        { value: 'they/them', label: 'they/them' },
        { value: 'she/they', label: 'she/they' },
        { value: 'he/they', label: 'he/they' },
      ],
      sensitivity: 'public',
    },
    {
      id: 'ethnicity',
      label: 'Ethnicity',
      type: FieldType.SELECT,
      tags: ['demographics', 'dei'],
      priority: 70,
      namespace: 'demographics',
      allowCustomOptions: true,
      options: [
        { value: 'asian', label: 'Asian' },
        { value: 'black', label: 'Black or African American' },
        { value: 'hispanic', label: 'Hispanic or Latino' },
        { value: 'native_american', label: 'Native American' },
        { value: 'pacific_islander', label: 'Pacific Islander' },
        { value: 'white', label: 'White' },
        { value: 'two_or_more', label: 'Two or More Races' },
        { value: 'prefer_not_to_say', label: 'Prefer Not to Say' },
      ],
      sensitivity: 'sensitive_pii',
    },
    {
      id: 'nationality',
      label: 'Nationality',
      type: FieldType.TEXT,
      tags: ['demographics'],
      priority: 65,
      namespace: 'demographics',
      sensitivity: 'sensitive_pii',
    },
    {
      id: 'dateOfBirth',
      label: 'Date of Birth',
      type: FieldType.DATE,
      tags: ['demographics'],
      priority: 60,
      namespace: 'demographics',
      sensitivity: 'sensitive_pii',
    },
    {
      id: 'ageGroup',
      label: 'Age Group',
      type: FieldType.SELECT,
      tags: ['demographics', 'dei'],
      priority: 55,
      namespace: 'demographics',
      options: [
        { value: 'under_25', label: 'Under 25' },
        { value: '25_34', label: '25-34' },
        { value: '35_44', label: '35-44' },
        { value: '45_54', label: '45-54' },
        { value: '55_64', label: '55-64' },
        { value: '65_plus', label: '65+' },
      ],
      sensitivity: 'sensitive_pii',
    },
    {
      id: 'generation',
      label: 'Generation',
      type: FieldType.SELECT,
      tags: ['demographics'],
      priority: 50,
      namespace: 'demographics',
      options: [
        { value: 'gen_z', label: 'Gen Z' },
        { value: 'millennial', label: 'Millennial' },
        { value: 'gen_x', label: 'Gen X' },
        { value: 'boomer', label: 'Baby Boomer' },
      ],
      sensitivity: 'sensitive_pii',
    },
    {
      id: 'veteranStatus',
      label: 'Veteran Status',
      type: FieldType.BOOLEAN,
      tags: ['demographics', 'dei'],
      priority: 45,
      namespace: 'demographics',
      sensitivity: 'sensitive_pii',
    },
    {
      id: 'disabilityStatus',
      label: 'Disability Status',
      type: FieldType.SELECT,
      tags: ['demographics', 'dei'],
      priority: 40,
      namespace: 'demographics',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'prefer_not_to_say', label: 'Prefer Not to Say' },
      ],
      sensitivity: 'sensitive_pii',
    },
    {
      id: 'maritalStatus',
      label: 'Marital Status',
      type: FieldType.SELECT,
      tags: ['demographics'],
      priority: 35,
      namespace: 'demographics',
      options: [
        { value: 'single', label: 'Single' },
        { value: 'married', label: 'Married' },
        { value: 'divorced', label: 'Divorced' },
        { value: 'widowed', label: 'Widowed' },
        { value: 'domestic_partner', label: 'Domestic Partner' },
      ],
      sensitivity: 'sensitive_pii',
    },
    {
      id: 'diversityFlags',
      label: 'Diversity Flags',
      type: FieldType.MULTI_SELECT,
      tags: ['demographics', 'dei'],
      priority: 30,
      namespace: 'demographics',
      allowCustomOptions: true,
      sensitivity: 'sensitive_pii',
    },
  ],
};
